import {
  Box,
  Button,
  createStyles,
  makeStyles,
  Typography,
} from "@material-ui/core";
import PlaceIcon from "@material-ui/icons/Place";
import FormAlert from "legacy-components/form/container/FormAlert";
import { InputContainer } from "modus/components/form/input-container/InputContainer";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import ApiService from "../../services/ApiService";
import { UpdateDeploymentProps } from "../../components/DeploymentDetails";
import { TrimbleColors } from "legacy-components/TrimbleColors";
import {
  formatTargetCloudProviderLabel,
  getGatewayBrandNameFromGatewayRegionKey,
} from "../../config/deploymentRegions";
import { getResolvedEnv } from "../../components/EnvBadge";

const useStyles = makeStyles((theme) =>
  createStyles({
    formLayout: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
    },
    formContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    submitLoader: {
      position: "absolute",
      zIndex: 2,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.32)",
    },
    sectionHeading: {
      fontFamily: "'Open Sans', sans-serif",
      fontWeight: 600,
      fontSize: 14,
      lineHeight: "20px",
      color: "#252A2E",
      marginBottom: theme.spacing(1.5),
    },
    sectionBlock: {
      marginBottom: theme.spacing(3),
    },
    deploymentRoutingBlock: {
      backgroundColor: TrimbleColors.Gray1,
      borderRadius: 4,
      padding: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.5),
    },
    deploymentRoutingRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
    },
    deploymentRoutingIcon: {
      fontSize: 20,
      color: TrimbleColors.Gray6,
      marginTop: 2,
      flexShrink: 0,
    },
    deploymentRoutingLabels: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.25),
    },
    deploymentRoutingPrimaryLine: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: 14,
      lineHeight: "20px",
      color: "#252A2E",
      fontWeight: 600,
    },
    deploymentRoutingHelper: {
      fontFamily: "'Open Sans', sans-serif",
      fontSize: 12,
      lineHeight: "16px",
      color: TrimbleColors.Gray6,
      marginTop: theme.spacing(0.5),
    },
    connectionHelperText: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
      fontSize: 12,
      lineHeight: "16px",
      color: TrimbleColors.Gray6,
    },
    actions: {
      borderTop: `1px solid ${TrimbleColors.Gray2}`,
      paddingTop: theme.spacing(3),
      marginTop: theme.spacing(3),
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(2),
    },
    cancelButton: { textTransform: "none" as const },
    submitButton: { textTransform: "none" as const },
  })
);

export interface UpdateDeploymentFormRegionalProps {
  onCancel: () => void;
  onSuccess?: () => void;
  basePath: string;
  version: string;
  availableRegions: { key: string; value: string }[];
  updateDeploymentInitialValue?: UpdateDeploymentProps;
}

const UpdateDeploymentFormRegional: React.FC<UpdateDeploymentFormRegionalProps> = ({
  onCancel,
  onSuccess,
  updateDeploymentInitialValue,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const [success, setSuccess] = useState(false);
  const [loader, setLoader] = useState(false);

  const FormErrorViewer = ({ errors }: { errors?: string[] }) => {
    return (
      <>
        {errors?.map((err, index) => (
          <div key={index}>{err}</div>
        ))}
      </>
    );
  };

  const [error, setError] = useState({
    is_error: false,
    message: "",
  });
  const [formValues, setFormValues] = useState({
    targetEndpoint: updateDeploymentInitialValue?.targetEndpoint ?? "",
    timeout: String(updateDeploymentInitialValue?.timeout ?? "30"),
  });
  const [hasFieldError2, setHasFieldError2] = useState(false);

  const handleSetError2 = (value: boolean) => {
    setHasFieldError2(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const gatewayRegion =
    updateDeploymentInitialValue?.gatewayRegion ??
    updateDeploymentInitialValue?.region ??
    "";
  const targetRegion =
    updateDeploymentInitialValue?.targetRegion ?? gatewayRegion;
  const targetProviderLabel = formatTargetCloudProviderLabel(
    updateDeploymentInitialValue?.targetProvider
  );
  const targetRegionHuman = targetRegion ? getResolvedEnv(targetRegion) : "";
  const deploymentRoutingLine1 =
    targetProviderLabel && targetRegionHuman
      ? `${targetProviderLabel} | ${targetRegionHuman}`
      : targetRegionHuman || "—";
  const gatewayBrand = getGatewayBrandNameFromGatewayRegionKey(gatewayRegion);
  const deploymentRoutingLine2 = `${gatewayBrand} gateway`;

  const updatePayload = {
    backendUrl: formValues.targetEndpoint,
    timeout: parseInt(formValues.timeout, 10) || 30,
  };
  const deploymentId = updateDeploymentInitialValue?.deploymentId ?? "";

  const { state: updateDeploymentState, subscribe: updateDeployment } =
    useAsyncAction(() =>
      ApiService.updateDeploymentV2(updatePayload, deploymentId)
    );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateDeployment();
  };

  useEffect(() => {
    const isLoaded = updateDeploymentState?.loaded;
    const hasError = updateDeploymentState?.error;
    if (isLoaded && !hasError && onSuccess) {
      onSuccess();
    }
  }, [updateDeploymentState, onSuccess]);

  return (
    <form
      className={styles.formLayout}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Box className={styles.formContainer}>
        {loader && <Box className={styles.submitLoader} />}
        {error.is_error && (
          <FormAlert severity="error">
            <FormErrorViewer errors={[error.message]} />
          </FormAlert>
        )}
        {success && (
          <FormAlert severity="success">
            Deployment updated successfully.
          </FormAlert>
        )}
        {updateDeploymentState?.error && (
          <FormAlert severity="error">
            {resolveAPIErrorMessage(updateDeploymentState.error)}
          </FormAlert>
        )}

        <Box className={styles.sectionBlock}>
          <Typography className={styles.sectionHeading}>
            Deployment routing
          </Typography>
          <Box className={styles.deploymentRoutingBlock}>
            <Box className={styles.deploymentRoutingRow}>
              <PlaceIcon className={styles.deploymentRoutingIcon} />
              <Box className={styles.deploymentRoutingLabels}>
                <Typography className={styles.deploymentRoutingPrimaryLine}>
                  {deploymentRoutingLine1}
                </Typography>
                <Typography className={styles.deploymentRoutingPrimaryLine}>
                  {deploymentRoutingLine2}
                </Typography>
                <Typography className={styles.deploymentRoutingHelper}>
                  Region cannot be changed after a deployment is created.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className={styles.sectionBlock}>
          <Typography className={styles.sectionHeading}>
            Connection settings
          </Typography>
          <InputContainer
            label={intl.formatMessage({ defaultMessage: "Backend URL" })}
            value={formValues.targetEndpoint}
            onChange={handleChange}
            name="targetEndpoint"
            setError={handleSetError2}
            validate={{
              required: true,
              minLength: 3,
              maxLength: 255,
              pattern:
                "^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)[a-z0-9]+([-.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/[^?]*)?(\\?([a-zA-Z0-9_-]+=[a-zA-Z0-9_-]+)(&[a-zA-Z0-9_-]+=[a-zA-Z0-9_-]+)*)?$",
            }}
            required
          />
          <Typography className={styles.connectionHelperText}>
            The destination URL where your API is hosted. e.g.{" "}
            https://api.example.com
          </Typography>
          <InputContainer
            label={intl.formatMessage({ defaultMessage: "Connect timeout" })}
            placeholder="30"
            value={formValues.timeout}
            onChange={handleChange}
            name="timeout"
            validate={{
              required: true,
              allowSpace: false,
              pattern: "^[0-9]+$",
              min: 1,
              max: 55,
              patternMessage: "Only numeric values are allowed",
            }}
            setError={handleSetError2}
            required
          />
        </Box>

        <Box className={styles.actions}>
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            className={styles.cancelButton}
            disableElevation
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={styles.submitButton}
            disableElevation
            disabled={
              hasFieldError2 ||
              !formValues.targetEndpoint ||
              !formValues.timeout ||
              updateDeploymentState?.loading
            }
          >
            <FormattedMessage defaultMessage="Update" />
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default UpdateDeploymentFormRegional;
